import { sendAnalytics } from '@ezugi/analytics/sendAnalytics';
import { GE } from '@ezugi/constants';
import { DeviceInfo } from '@ezugi/utils';

import {
  EVO_WRAPPER_BALANCE_UPDATE_SUBTYPE,
  EVO_WRAPPER_CLEAR_DIALOG_STORE_SUBTYPE,
  EVO_WRAPPER_CONTINUE_WITHOUT_VIDEO_SUBTYPE,
  EVO_WRAPPER_INIT_SUBTYPE,
  EVO_WRAPPER_MESSAGE_TYPE,
  EVO_WRAPPER_PUSH_ANALYTICS_SUBTYPE,
  EVO_WRAPPER_RECONNECT_EZUGI_WEBSOCKET_SUBTYPE,
  EVO_WRAPPER_TOGGLE_FULLSCREEN_SUBTYPE,
  EVO_WRAPPER_VOLUME_UPDATE_SUBTYPE,
  TRIGGER_UPDATE_BALANCE_SOCKET_MESSAGE,
} from '../constants/oss';
import {
  configActions,
  userActions,
  tableActions,
  uiActions,
  socketActions,
  videoActions,
  notificationActions,
  dialogActions,
  mediaActions,
} from '../store/actions';
import {
  clientIdSelector,
  dialogSelector,
  evoWalletBalanceVersionSelector,
  gameIdSelector,
  tableIdSelector,
  volumeSelector,
} from '../store/selectors';
import { errorCodeSelector } from '../store/selectors/error';

const { config } = configActions;
const { user } = userActions;
const { table } = tableActions;
const { ui } = uiActions;
const { socket } = socketActions;
const { video } = videoActions;
const { notification } = notificationActions;
const { dialog } = dialogActions;
const { volume } = mediaActions;

export function handleEvoWrapperMessages(event, state$) {
  const { data: message } = event;
  if (message?.type !== EVO_WRAPPER_MESSAGE_TYPE) return;

  switch (message?.subType) {
  case EVO_WRAPPER_INIT_SUBTYPE: {
    const errorCode = errorCodeSelector(state$.source.value);
    state$.dispatch(config.update({ isEvoWrapped: true }));

    if (errorCode === 0) {
      state$.dispatch(table.set(
        {
          externalTableName: message.payload.tableName,
          externalTableId: message.payload.tableId,
        }
      ));
    }

    /**
     * As @ezugi/hooks are not connected to the store
     * we need to do this in order to know inside the hook if we are dealing
     * with a wrapped game
     */

    window.__EZUGI_EVO_WRAPPER__ = true;

    /**
     * Evolution has its own enable/disable configs for fullscreen, tipping, etc
     * we receive the config boolean from Evo's system, and then we overwrite our own config
     * for wrapped games
     */
    const { config: { widgets } } = message.payload;

    state$.dispatch(config.update({ widgets }));
    break;
  }

  case EVO_WRAPPER_BALANCE_UPDATE_SUBTYPE: {
    const { evoWrapperBalance, evoWrapperBalanceVersion } = message.payload;
    const evoWalletBalanceVersion = evoWalletBalanceVersionSelector(state$.source.value);
    const ezugiTableId = tableIdSelector(state$.source.value);
    const gameId = gameIdSelector(state$.source.value);
    const clientId = clientIdSelector(state$.source.value);

    state$.dispatch(user.set({ evoWrapperBalance, evoWrapperBalanceVersion }));
    const shouldRequestBalanceUpdateFromEngine = (evoWrapperBalanceVersion > evoWalletBalanceVersion)
    || (evoWrapperBalanceVersion === undefined);

    if (shouldRequestBalanceUpdateFromEngine) {
      state$.dispatch(socket.send(
        {
          MessageType: TRIGGER_UPDATE_BALANCE_SOCKET_MESSAGE,
          destination: GE.OUT.SOCKET_SEND.DESTINATION.PLAYER,
          tableId: ezugiTableId,
          gameType: gameId,
          ClientId: clientId,
        }
      ));
    }
    break;
  }

  case EVO_WRAPPER_TOGGLE_FULLSCREEN_SUBTYPE:
    /**
     * We compare the fullscreen state of Evo's wrapper with our own
     * Necessary when user presses ESC button to exit fullscreen on wrapped games
     * We need to keep both states in sync (Wrapper's fullscreen and Ezugi Game Fullscreen)
     */
    state$.dispatch(
      ui.set({ isParentWindowFullscreen: message.payload.isFullscreen })
    );
    break;

  case EVO_WRAPPER_RECONNECT_EZUGI_WEBSOCKET_SUBTYPE:
    state$.dispatch(socket.request());
    state$.dispatch(video.resume());
    break;

  case EVO_WRAPPER_PUSH_ANALYTICS_SUBTYPE:
    sendAnalytics(message.payload);
    break;

  case EVO_WRAPPER_CONTINUE_WITHOUT_VIDEO_SUBTYPE:
    state$.dispatch(notification.add({
      message: 'notifications.video_unavailable',
      delay: 99999999,
      trigger: `${video.error.type}`,
    }));
    state$.dispatch(video.reconnect());
    break;

  case EVO_WRAPPER_CLEAR_DIALOG_STORE_SUBTYPE: {
    const dialogName = dialogSelector(state$.source.value)?.name;
    /**
     * Only a few generic dialogs are rendered via Evo Wrapper
     * By doing the check below, we dispatch dialog.remove
     * and clear the store only when a button is pressed on the OSS popup that
     * matches what we currently have in the store(Evo's popups go away once you click on any button,
     * by doing the below, we ensure that an Evo popup doesn't instantly close a popup that is not rendered via OSS Wrapper)
     * E.g Let's say we display Low Balance Dialog via OSS Wrapper, then a new dialog is added (which is not rendered on wrapper's side), let's say,
     * incident dialog. If we do not do the check below, when the low balance popup will be dismissed, the incident dialog will also be dismissed
     */
    if (message.payload.id !== `Ezugi${dialogName}`) return;

    state$.dispatch(dialog.remove());
    break;
  }

  case EVO_WRAPPER_VOLUME_UPDATE_SUBTYPE: {
    const volumeState = volumeSelector(state$.source.value);
    /**
     * We do not allow the wrapper to change our game's volume state
     * Because we do not have a "master volume" control on iOS
     */
    if (DeviceInfo.isIOS || (message.payload.muted === volumeState?.muted)) return;
    state$.dispatch(volume.set({ ...volumeState, muted: message.payload.muted }));
    break;
  }

  default:
    // eslint-disable-next-line no-console
    console.warn(`Unhandled message: ${message?.subType}`);
    break;
  }
}
